import { z } from "zod";
import type { BookFormat } from "./formats";

/*
 * Send to Kindle: limits and file selection shared by server and UI.
 * Amazon's rules (assumptions, see the add-send-to-kindle design): approved sender required,
 * 50 MB per email, EPUB, PDF and TXT accepted; MOBI/AZW3 are no longer accepted.
 */

/** 50 MB in bytes (the raw file size; base64 encoding makes the message about 37% larger). */
export const KINDLE_MAX_BYTES = 52_428_800;
export const KINDLE_MAX_MB = 50;
export const KINDLE_FORMATS = ["epub", "pdf", "txt"] as const satisfies readonly BookFormat[];
export type KindleFormat = (typeof KINDLE_FORMATS)[number];
export const KINDLE_DOMAINS = ["kindle.com", "free.kindle.com"] as const;
export const KINDLE_EMAIL_MAX_LENGTH = 254;
export const KINDLE_RATE_LIMIT = 20;
export const KINDLE_RATE_WINDOW_MS = 60 * 60 * 1000;
export const KINDLE_HISTORY_PAGE_SIZE = 20;
/** Amazon page where the approved sender list is managed (shown in the settings). */
export const AMAZON_PERSONAL_DOCUMENTS_URL = "https://www.amazon.com/mycd";

/** Why a book cannot be sent (reasons of `GET /api/books/:id/kindle`, in priority order). */
export const KINDLE_UNAVAILABLE_REASONS = [
  "kindle_email_missing",
  "no_kindle_compatible_format",
  "file_too_large",
] as const;
export type KindleUnavailableReason = (typeof KINDLE_UNAVAILABLE_REASONS)[number];

export const KINDLE_DELIVERY_STATUSES = ["pending", "sent", "failed"] as const;
export type KindleDeliveryStatus = (typeof KINDLE_DELIVERY_STATUSES)[number];

/** Classified SMTP failures (also the API error codes of the admin test email). */
export const SMTP_ERROR_CODES = [
  "smtp_connection_failed",
  "smtp_auth_failed",
  "smtp_tls_required",
  "smtp_rejected",
  "smtp_send_failed",
] as const;
export type SmtpErrorCode = (typeof SMTP_ERROR_CODES)[number];

/**
 * A Kindle address: a valid email with the domain exactly `kindle.com` or `free.kindle.com`,
 * stored lower-cased. Errors are reported as `kindle_email_invalid`.
 */
export const kindleEmailSchema = z
  .string({ error: "kindle_email_invalid" })
  .trim()
  .max(KINDLE_EMAIL_MAX_LENGTH, { error: "kindle_email_invalid" })
  .pipe(z.email({ error: "kindle_email_invalid" }))
  .transform((value) => value.toLowerCase())
  .refine(
    (value) =>
      (KINDLE_DOMAINS as readonly string[]).includes(value.slice(value.lastIndexOf("@") + 1)),
    { error: "kindle_email_invalid" },
  );

export const kindleEmailUpdateSchema = z.object({
  email: z.union([z.string({ error: "invalid" }), z.null()], { error: "invalid" }),
});

export interface KindleFileCandidate {
  id: string;
  format: string;
  sizeBytes: number;
  /** Epoch milliseconds (or a Date) the file was added. */
  createdAt: number | Date;
}

export type KindleSelection<F extends KindleFileCandidate> =
  | { ok: true; file: F; oversizedFormats: KindleFormat[] }
  | { ok: false; reason: "no_kindle_compatible_format" | "file_too_large" };

const time = (value: number | Date) => (value instanceof Date ? value.getTime() : value);

/**
 * Picks the file to send: the newest EPUB of at most 50 MB, otherwise the newest PDF, otherwise the
 * newest TXT (each of at most 50 MB). `kepub` and every other format are never sent. `oversizedFormats` lists formats that exist
 * but were skipped because of their size.
 */
export function selectKindleFile<F extends KindleFileCandidate>(files: F[]): KindleSelection<F> {
  const oversized: KindleFormat[] = [];
  for (const format of KINDLE_FORMATS) {
    const ofFormat = files.filter((file) => file.format === format);
    const eligible = ofFormat
      .filter((file) => file.sizeBytes <= KINDLE_MAX_BYTES)
      .sort((a, b) => time(b.createdAt) - time(a.createdAt) || (a.id < b.id ? 1 : -1));
    const chosen = eligible[0];
    if (chosen) return { ok: true, file: chosen, oversizedFormats: oversized };
    if (ofFormat.length > 0) oversized.push(format);
  }
  return {
    ok: false,
    reason: oversized.length > 0 ? "file_too_large" : "no_kindle_compatible_format",
  };
}

// --- DTOs ---------------------------------------------------------------------------

export interface EmailStatusDto {
  configured: boolean;
  /** Bare sender address (without display name); only when configured. */
  senderAddress?: string;
}

export interface AdminEmailDto extends EmailStatusDto {
  host?: string;
  port?: number;
  mode?: "implicit_tls" | "starttls";
  /** Full `From` header value. */
  from?: string;
  authConfigured?: boolean;
  /** Whether invitation and reset links can be sent by email (SMTP and a public URL are set). */
  linkEmails: LinkEmailAvailabilityDto;
}

export type LinkEmailAvailabilityDto =
  | { available: true }
  | { available: false; reason: "email_not_configured" | "public_url_not_configured" };

export interface KindleAddressDto {
  email: string | null;
}

export type KindleAvailabilityDto =
  | {
      available: true;
      format: KindleFormat;
      sizeBytes: number;
      oversizedFormats: KindleFormat[];
    }
  | { available: false; reason: KindleUnavailableReason };

export interface KindleDeliveryDto {
  id: string;
  /** `null` once the book was deleted. */
  bookId: string | null;
  bookTitle: string;
  format: string;
  sizeBytes: number;
  recipient: string;
  status: KindleDeliveryStatus;
  errorCode: string | null;
  createdAt: string;
  sentAt: string | null;
}

export interface KindleDeliveryListDto {
  items: KindleDeliveryDto[];
  page: number;
  pageSize: number;
  total: number;
}
